import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setIntegrationModalState } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/modal";
import {
    selectIsDisabledIntegrationModal,
    selectModalNotification,
    selectModalToggleName,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/managementSelectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import {
    putHsCompaniesToggleThunk,
    putHsContactsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/toggles";
import {
    putSfAccountsToggleThunk,
    putSfCompanyAsLeadsToggleThunk,
    putSfContactsToggleThunk,
    putSfLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    putDynamicsAccountsToggleThunk,
    putDynamicsContactsToggleThunk,
    putDynamicsLeadsToggleThunk,
    putDynamicsCompanyAsLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/toggles";
import { allTrackers } from "app/services/track/track";

export const useExportDisabledModalProps = (
    integrationName: Integration,
): {
    setModalState(state: boolean): void;
    onConfirm(state: boolean): void;
    isOpen: boolean;
    isDisabledExport: boolean;
    toggleName: ExportName | null;
} => {
    const dispatch = useDispatch();
    const toggleName = useSelector(selectModalToggleName);

    const toggles = {
        salesforce: {
            accounts: putSfAccountsToggleThunk,
            contacts: putSfContactsToggleThunk,
            leads: putSfLeadsToggleThunk,
            companyAsLeads: putSfCompanyAsLeadsToggleThunk,
        },
        hubspot: { accounts: putHsCompaniesToggleThunk, contacts: putHsContactsToggleThunk },
        dynamics: {
            accounts: putDynamicsAccountsToggleThunk,
            contacts: putDynamicsContactsToggleThunk,
            leads: putDynamicsLeadsToggleThunk,
            companyAsLeads: putDynamicsCompanyAsLeadsToggleThunk,
        },
    };

    const setModalState = (state: boolean) => dispatch(setIntegrationModalState(state));

    const onConfirm = useCallback(
        (state: boolean) => {
            if (toggleName) {
                const toggleFunc = (toggles[integrationName] as Record<ExportName, typeof putSfAccountsToggleThunk>)[
                    toggleName
                ];
                dispatch(toggleFunc(state));
                allTrackers.dispatchTrackEvent(
                    `${integrationName} integration`,
                    `enable export`,
                    `export/${toggleName}/off`,
                );
            }
        },
        [integrationName, toggleName],
    );

    const isOpen = useSelector(selectModalNotification);
    const isDisabledExport = useSelector(selectIsDisabledIntegrationModal);

    return {
        setModalState,
        onConfirm,
        isOpen,
        isDisabledExport,
        toggleName,
    };
};
